import { cn } from "@/lib/cn";
import { CheckIcon } from "./icons";

type Props = {
  steps: string[];
  current: number; // 0-based
  complete?: number[];
  onJump?: (i: number) => void;
};

/**
 * Three-up stepper used at the top of the Customise → Approval → Order flow.
 * Active step uses the studio terracotta; done steps use the success green.
 */
export function Stepper({ steps, current, complete = [], onJump }: Props) {
  return (
    <ol
      data-chrome
      className="mb-6 flex list-none overflow-hidden rounded-lg border border-[var(--border-1)] bg-white p-0"
    >
      {steps.map((label, i) => {
        const done = complete.includes(i) || i < current;
        const active = i === current;
        const locked = i > current && !complete.includes(i);
        return (
          <li
            key={label}
            className={cn(
              "flex flex-1 items-center gap-2.5 border-r border-[var(--border-1)] px-4 py-3 text-sm last:border-r-0",
              "transition-colors",
              active && "bg-[var(--studio-wash)] font-semibold text-fg-1",
              done && !active && "text-fg-1",
              !done && !active && "text-fg-3",
              locked ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-bg-1",
            )}
            onClick={() => !locked && onJump && onJump(i)}
          >
            <span
              className={cn(
                "grid h-6 w-6 flex-shrink-0 place-items-center rounded-full text-xs font-semibold",
                done ? "text-white" : active ? "text-white" : "bg-bg-2 text-fg-3",
              )}
              style={
                done
                  ? { background: "var(--ok)" }
                  : active
                    ? { background: "var(--studio)" }
                    : undefined
              }
            >
              {done ? <CheckIcon width={12} height={12} strokeWidth={2.5} /> : i + 1}
            </span>
            <span className="truncate">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}
